import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './stores/authStore'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import Login from './pages/Login'
import Register from './pages/Register'

// Lazy load pages
const Dashboard = lazy(() => import('./pages/Dashboard'))
const CatalogBrowser = lazy(() => import('./pages/CatalogBrowser'))
const QuizSession = lazy(() => import('./pages/QuizSession'))
const QuizResult = lazy(() => import('./pages/QuizResult'))
const History = lazy(() => import('./pages/History'))
const Bookmarks = lazy(() => import('./pages/Bookmarks'))
const WrongQuestions = lazy(() => import('./pages/WrongQuestions'))

const Readiness = lazy(() => import('./pages/Readiness'))

// Lazy load admin pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'))
const QuestionManager = lazy(() => import('./pages/admin/QuestionManager'))
const QuestionEditor = lazy(() => import('./pages/admin/QuestionEditor'))
const UserManager = lazy(() => import('./pages/admin/UserManager'))
const ImportExport = lazy(() => import('./pages/admin/ImportExport'))
const QuestionAnalytics = lazy(() => import('./pages/admin/QuestionAnalytics'))
const QuestionFeedback = lazy(() => import('./pages/admin/QuestionFeedback'))

function Loading() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
    </div>
  )
}

export default function App() {
  const { checkAuth, initialized } = useAuthStore()

  useEffect(() => {
    checkAuth()
  }, [])

  if (!initialized) {
    return <Loading />
  }

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route element={<Layout />}>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Protected user routes */}
          <Route path="/" element={
            <ProtectedRoute><Dashboard /></ProtectedRoute>
          } />
          <Route path="/practice" element={
            <ProtectedRoute><CatalogBrowser /></ProtectedRoute>
          } />
          <Route path="/quiz/:id" element={
            <ProtectedRoute><QuizSession /></ProtectedRoute>
          } />
          <Route path="/quiz/:id/result" element={
            <ProtectedRoute><QuizResult /></ProtectedRoute>
          } />
          <Route path="/history" element={
            <ProtectedRoute><History /></ProtectedRoute>
          } />
          <Route path="/bookmarks" element={
            <ProtectedRoute><Bookmarks /></ProtectedRoute>
          } />
          <Route path="/wrong-questions" element={
            <ProtectedRoute><WrongQuestions /></ProtectedRoute>
          } />
          <Route path="/readiness" element={
            <ProtectedRoute><Readiness /></ProtectedRoute>
          } />

          {/* Admin routes */}
          <Route path="/admin" element={
            <ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>
          } />
          <Route path="/admin/questions" element={
            <ProtectedRoute adminOnly><QuestionManager /></ProtectedRoute>
          } />
          <Route path="/admin/questions/:id" element={
            <ProtectedRoute adminOnly><QuestionEditor /></ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute adminOnly><UserManager /></ProtectedRoute>
          } />
          <Route path="/admin/import" element={
            <ProtectedRoute adminOnly><ImportExport /></ProtectedRoute>
          } />
          <Route path="/admin/analytics" element={
            <ProtectedRoute adminOnly><QuestionAnalytics /></ProtectedRoute>
          } />
          <Route path="/admin/feedback" element={
            <ProtectedRoute adminOnly><QuestionFeedback /></ProtectedRoute>
          } />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  )
}
